/**
 * A load with three honest states.
 *
 * Every content read in the shell can fail — an offline launch, a package that
 * never made it into the cache — and the screens are required to say so rather
 * than spin. Nothing here retries: retry is the delivery layer's business.
 */
import { useEffect, useState } from 'react';

export type Async<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'error'; readonly error: Error };

const LOADING: Async<never> = { status: 'loading' };

/**
 * Runs `load` whenever `deps` change. Returning `null` from `load` means there
 * is nothing to load yet and the result stays `loading`.
 */
export function useAsync<T>(load: () => Promise<T> | null, deps: readonly unknown[]): Async<T> {
  const [result, setResult] = useState<Async<T>>(LOADING);

  useEffect(() => {
    let live = true;
    const promise = load();
    if (!promise) {
      setResult(LOADING);
      return;
    }
    setResult(LOADING);
    promise.then(
      (value) => {
        if (live) setResult({ status: 'ready', value });
      },
      (cause: unknown) => {
        if (live) {
          setResult({
            status: 'error',
            error: cause instanceof Error ? cause : new Error(String(cause)),
          });
        }
      },
    );
    return () => {
      live = false;
    };
    // `load` is intentionally not a dependency: callers pass an inline closure,
    // and the dependency list they supply is the one that means anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return result;
}
